import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, ArrowLeft, ExternalLink, Clock, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const STAGE_BADGE = {
  draft: "bg-slate-200 text-slate-700", pending_org: "bg-orange-100 text-orange-700",
  pending_cbr: "bg-blue-100 text-blue-700", correction_required: "bg-red-100 text-red-700",
  corrected: "bg-indigo-100 text-indigo-700", approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-slate-300 text-slate-700",
};

export default function AdminGameReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showHit, setShowHit] = useState(true);
  const [showPit, setShowPit] = useState(true);
  const [showFld, setShowFld] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/live/admin/games/${id}/review`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (endpoint, action, needReason) => {
    let reason = null;
    if (needReason) {
      reason = window.prompt(`Reason for ${action.replace("_", " ")}:`);
      if (!reason) return;
    }
    setBusy(true);
    try {
      await api.post(`/live/games/${id}/${endpoint}`, { action, reason });
      toast.success(`Game ${action.replace("_", " ")} done`);
      window.dispatchEvent(new Event("cbr:approvals-changed"));
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <AdminLayout><div className="p-8 label-overline text-slate-500">Loading review…</div></AdminLayout>;
  if (!d) return <AdminLayout><div className="p-8">Game not found. <Link to="/admin/games/approval" className="text-[#E63946] underline">Back to queue</Link></div></AdminLayout>;

  const s = d.summary; const ap = d.approval; const v = d.validation;
  const stage = ap.approval_status;
  const canOrg = stage === "pending_org";
  const canCbr = ["pending_cbr", "corrected", "correction_required"].includes(stage) || ap.pending_revision;
  const isCorrection = stage === "correction_required" && !ap.pending_revision;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-6 pb-32" data-testid="admin-game-review">
        <button onClick={() => navigate("/admin/games/approval")} className="inline-flex items-center gap-1 text-xs text-slate-500 uppercase tracking-wider mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back to queue</button>

        {/* VALIDATION PANEL (top) */}
        <div data-testid="review-validation" className={`rounded-sm p-4 mb-5 border ${v.status === "error" ? "bg-red-50 border-red-200" : v.status === "warning" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="flex items-center gap-3">
            {v.status === "error" ? <XCircle className="w-6 h-6 text-red-500" /> : v.status === "warning" ? <AlertTriangle className="w-6 h-6 text-amber-500" /> : <CheckCircle2 className="w-6 h-6 text-emerald-500" />}
            <div>
              <div className="font-display text-lg uppercase tracking-tight">{v.status === "error" ? "Needs Attention — Blocking Errors" : v.status === "warning" ? "Review Warnings" : "This game is clean"}</div>
              <div className="text-xs text-slate-600">{v.errors} blocking error{v.errors === 1 ? "" : "s"} · {v.warnings} warning{v.warnings === 1 ? "" : "s"} · {v.lines} player line{v.lines === 1 ? "" : "s"}</div>
            </div>
          </div>
          {d.team_totals?.warnings?.length > 0 && <ul className="mt-2 text-xs text-amber-700 list-disc pl-5">{d.team_totals.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          {d.team_totals?.errors?.length > 0 && <ul className="mt-1 text-xs text-red-700 list-disc pl-5">{d.team_totals.errors.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        </div>

        {/* GAME SUMMARY */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${STAGE_BADGE[stage] || "bg-slate-100"}`} data-testid="review-stage">{ap.approval_status}</span>
                {ap.pending_revision && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-indigo-100 text-indigo-700">Revision #{ap.revision_number}</span>}
                <span className="text-xs text-slate-400">v{ap.approval_version}</span>
              </div>
              <h1 className="font-display text-3xl uppercase tracking-tight mt-2">{s.home_team} <span className="text-slate-300">vs</span> {s.away_team}</h1>
              <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-x-4">
                <span>{s.date || "—"}</span>{s.tournament && <span>{s.tournament}</span>}{s.game_type && <span className="uppercase">{s.game_type}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-data text-4xl text-[#0A192F]">{s.final_score}</div>
              <a href={s.slug ? `/games/${s.game_id}` : `/games/${s.game_id}`} target="_blank" rel="noreferrer" data-testid="review-preview-public" className="inline-flex items-center gap-1 text-xs text-[#E63946] mt-1 hover:underline">Preview Public View <ExternalLink className="w-3 h-3" /></a>
            </div>
          </div>
          {ap.rejection_reason && <div className="mt-3 text-xs bg-red-50 text-red-700 rounded-sm p-2">Rejection reason: {ap.rejection_reason}</div>}
          {ap.correction_reason && <div className="mt-3 text-xs bg-amber-50 text-amber-700 rounded-sm p-2">Correction requested: {ap.correction_reason}</div>}
          {ap.active_approved_version && ap.pending_revision && <div className="mt-3 text-xs bg-indigo-50 text-indigo-700 rounded-sm p-2">Last approved version stays public until this revision is approved.</div>}
        </div>

        {/* PLAYER TABLES */}
        <StatSection title="Player Hitting" open={showHit} toggle={() => setShowHit(!showHit)} testid="review-hitting"
          cols={["Player", "PA", "AB", "H", "2B", "3B", "HR", "RBI", "BB", "SO", "AVG", "OPS"]}
          rows={d.hitting} render={(r) => [r.name, r.pa, r.ab, r.h, r.doubles, r.triples, r.hr, r.rbi, r.bb, r.so, fmt(r.avg), fmt(r.ops)]} />
        <StatSection title="Player Pitching" open={showPit} toggle={() => setShowPit(!showPit)} testid="review-pitching"
          cols={["Player", "IP", "H", "R", "ER", "BB", "SO", "ERA", "WHIP"]}
          rows={d.pitching} render={(r) => [r.name, r.ip, r.h, r.r, r.er, r.bb, r.so, fmt2(r.era), fmt2(r.whip)]} />
        <StatSection title="Player Fielding" open={showFld} toggle={() => setShowFld(!showFld)} testid="review-fielding"
          cols={["Player", "Pos", "PO", "A", "E", "DP", "FLD%"]}
          rows={d.fielding} render={(r) => [r.name, r.position, r.po, r.a, r.e, r.dp, fmt(r.fielding_pct)]} />

        {/* AUDIT TIMELINE */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 mt-5" data-testid="review-audit">
          <div className="label-overline text-[#0A192F] mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Audit History</div>
          {d.audit.length === 0 ? <p className="text-sm text-slate-400">No history yet.</p> : (
            <ol className="relative border-l border-slate-200 pl-5 space-y-3">
              {d.audit.map((a, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[23px] top-1 w-2.5 h-2.5 rounded-full bg-[#E63946]" />
                  <div className="text-sm font-semibold text-[#0A192F]">{(a.action || "").replace(/_/g, " ")} {a.approval_version ? <span className="text-[10px] text-slate-400">v{a.approval_version}</span> : null}</div>
                  <div className="text-xs text-slate-500">{a.actor_email || "system"} · {a.actor_role || "—"} · {a.at ? new Date(a.at).toLocaleString("en-CA") : ""}</div>
                  {a.reason && <div className="text-xs text-slate-600 italic mt-0.5">"{a.reason}"</div>}
                </li>
              ))}
            </ol>
          )}
        </div>

        {(d.public_notes || d.internal_notes) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            {d.public_notes && <div className="bg-white border border-slate-200 rounded-sm p-4"><div className="label-overline mb-1">Public Notes</div><div className="text-sm text-slate-600 whitespace-pre-wrap">{typeof d.public_notes === "string" ? d.public_notes : JSON.stringify(d.public_notes)}</div></div>}
            {d.internal_notes && <div className="bg-amber-50 border border-amber-200 rounded-sm p-4"><div className="label-overline mb-1 text-amber-700">Internal Notes</div><div className="text-sm text-amber-800 whitespace-pre-wrap">{d.internal_notes}</div></div>}
          </div>
        )}
      </div>

      {/* STICKY ACTION BAR */}
      <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center gap-2 flex-wrap z-30" data-testid="review-actions">
        <span className="text-xs text-slate-400 mr-auto">Your role: <b className="text-[#0A192F]">{ap.your_role}</b></span>
        {canOrg && ap.your_role !== "team_admin" && (
          <>
            <ActionBtn testid="review-org-approve" onClick={() => act("org-review", "approve", false)} disabled={busy} className="bg-blue-600 text-white">Approve for CBR</ActionBtn>
            <ActionBtn testid="review-org-correction" onClick={() => act("org-review", "request_correction", true)} disabled={busy} className="border border-amber-400 text-amber-700">Request Correction</ActionBtn>
            <ActionBtn testid="review-org-reject" onClick={() => act("org-review", "reject", true)} disabled={busy} className="border border-red-400 text-red-600">Reject</ActionBtn>
          </>
        )}
        {canCbr && ap.your_role === "cbr_admin" && (
          <>
            <ActionBtn testid="review-approve" onClick={() => act("cbr-review", "approve", false)} disabled={busy || v.status === "error" || isCorrection} className="bg-emerald-600 text-white disabled:opacity-40" title={isCorrection ? "Awaiting corrected resubmission" : v.status === "error" ? "Fix blocking errors first" : ""}>Approve</ActionBtn>
            {!isCorrection && <ActionBtn testid="review-correction" onClick={() => act("cbr-review", "request_correction", true)} disabled={busy} className="border border-amber-400 text-amber-700">Request Correction</ActionBtn>}
            {!isCorrection && <ActionBtn testid="review-reject" onClick={() => act("cbr-review", "reject", true)} disabled={busy} className="border border-red-400 text-red-600">Reject</ActionBtn>}
          </>
        )}
        {stage === "approved" && ap.your_role === "cbr_admin" && (
          <ActionBtn testid="review-begin-revision" onClick={async () => { const reason = window.prompt("Reason for editing this approved game:"); if (!reason) return; setBusy(true); try { await api.post(`/live/games/${id}/begin-revision`, { reason }); toast.success("Revision opened"); window.dispatchEvent(new Event("cbr:approvals-changed")); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } }} disabled={busy} className="border border-slate-400 text-slate-700">Edit Approved Record</ActionBtn>
        )}
        <ActionBtn testid="review-recalc" onClick={load} disabled={busy} className="border border-slate-300 text-slate-500"><RefreshCw className="w-3.5 h-3.5 inline mr-1" />Recalculate</ActionBtn>
      </div>
    </AdminLayout>
  );
}

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(3).replace(/^0\./, "."));
const fmt2 = (v) => (v == null ? "—" : Number(v).toFixed(2));

function StatSection({ title, open, toggle, cols, rows, render, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm mb-4" data-testid={testid}>
      <button onClick={toggle} className="w-full flex items-center justify-between p-4 label-overline text-[#0A192F]">
        <span>{title} <span className="text-slate-400">({rows.length})</span></span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (rows.length === 0 ? <div className="px-4 pb-4 text-sm text-slate-400">No lines.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-max">
          <thead><tr className="bg-slate-50 border-y border-slate-200">{cols.map((c, i) => <th key={c} className={`p-2.5 label-overline ${i === 0 ? "text-left" : "text-right"}`}>{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, ri) => { const cells = render(r); return (
            <tr key={ri} className={`border-b border-slate-100 last:border-0 ${r.validation?.hard ? "bg-red-50" : ""}`}>
              {cells.map((c, ci) => <td key={ci} className={`p-2.5 font-data ${ci === 0 ? "text-left font-semibold text-[#0A192F]" : "text-right tabular-nums"}`}>{c ?? "—"}</td>)}
            </tr>); })}</tbody>
        </table></div>
      ))}
    </div>
  );
}

function ActionBtn({ children, className = "", testid, ...props }) {
  return <button {...props} data-testid={testid} className={`px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider ${className}`}>{children}</button>;
}
